import { useMemo } from 'react';
import { queryOptions, useQuery } from '@tanstack/react-query';
import { apiClient } from '../client';
import { orgFunctionKeys } from '../query-keys';
import { LOOKUP_LIST_CACHE } from '@/lib/query-client';
import { useAuthStore } from '@/stores/auth-store';
import {
  listOrgFunctionsContract,
  showOrgFunctionContract,
  listOrgFunctionMembersContract,
  listMyOrgFunctionsContract,
  type ListOrgFunctionsItem,
  type OrgFunction,
  type OrgFunctionKind,
  type OrgFunctionMember,
  type ListOrgFunctionsResponse,
  type ShowOrgFunctionResponse,
  type ListOrgFunctionMembersResponse,
  type ListMyOrgFunctionsResponse,
  type HeldOrgFunctionRef
} from '@bliks-io/contracts';

/**
 * Fetch the OrgFunctions list, optionally filtered by kind. Items carry the
 * server-derived `memberCount` (membership is never stored client- or
 * server-side — don't re-derive it from user docs).
 *
 * @example
 * ```tsx
 * function OrgFunctionsList() {
 *   const { data: orgFunctions, isLoading } = useOrgFunctions('team');
 *
 *   if (isLoading) return <Skeleton />;
 *   if (!orgFunctions) return null;
 *
 *   return <List items={orgFunctions} />;
 * }
 * ```
 */
export function useOrgFunctions(kind?: OrgFunctionKind) {
  const { token } = useAuthStore();

  return useQuery({
    ...orgFunctionsQueryOptions(kind),
    enabled: !!token
  });
}

/**
 * Query options for the (optionally kind-filtered) live list, shared between
 * the hook and route loaders that prefetch it so both sides stay on the same
 * cache key.
 */
export function orgFunctionsQueryOptions(kind?: OrgFunctionKind) {
  return queryOptions({
    queryKey: orgFunctionKeys.list({ kind }),
    queryFn: async ({ signal }): Promise<ListOrgFunctionsItem[]> => {
      const path = kind
        ? `${listOrgFunctionsContract.path}?kind=${kind}`
        : listOrgFunctionsContract.path;
      const { data } = await apiClient.get<ListOrgFunctionsResponse>(path, { signal });
      return data.data;
    }
  });
}

/**
 * Team-kind OrgFunctions anchored to a department, narrowed client-side from
 * the kind-filtered list (the endpoint has no department filter). Dept-less
 * OrgFunctions are org-wide and excluded.
 */
export function useDepartmentTeams(departmentId: string | undefined) {
  const { data, ...rest } = useOrgFunctions('team');
  const departmentTeams = useMemo(
    () => (data ?? []).filter((orgFunction) => orgFunction.department?._id === departmentId),
    [data, departmentId]
  );
  return { ...rest, data: departmentTeams };
}

/**
 * Fetch all OrgFunctions INCLUDING soft-deleted ones, for resolving entity refs
 * on old recordings (assignees, successors, helpers). Deleted OrgFunctions carry
 * a `deletedAt` timestamp so read surfaces can gray them out.
 *
 * Never use this for pickers/selectors — those must only offer live OrgFunctions
 * via {@link useOrgFunctions}.
 */
export function useOrgFunctionsForLookup(options?: { enabled?: boolean }) {
  const { token } = useAuthStore();

  return useQuery({
    ...orgFunctionsForLookupQueryOptions(),
    enabled: (options?.enabled ?? true) && !!token
  });
}

/**
 * Query options for the lookup variant, shared between the hook and route
 * loaders that prefetch the lookup so both sides stay on the same cache key.
 */
export function orgFunctionsForLookupQueryOptions() {
  return queryOptions({
    ...LOOKUP_LIST_CACHE,
    queryKey: orgFunctionKeys.list({ includeDeleted: true }),
    queryFn: async ({ signal }): Promise<OrgFunction[]> => {
      const { data } = await apiClient.get<ListOrgFunctionsResponse>(
        `${listOrgFunctionsContract.path}?includeDeleted=true`,
        { signal }
      );
      return data.data;
    }
  });
}

export const orgFunctionQueryOptions = (id: string) =>
  queryOptions({
    queryKey: orgFunctionKeys.detail(id),
    queryFn: async ({ signal }): Promise<OrgFunction> => {
      const { data } = await apiClient.get<ShowOrgFunctionResponse>(
        showOrgFunctionContract.path.replace(':id', id),
        { signal }
      );
      return data.data;
    }
  });

/**
 * Fetch a single OrgFunction by ID.
 *
 * @example
 * ```tsx
 * function OrgFunctionDetail({ orgFunctionId }: { orgFunctionId: string }) {
 *   const { data: orgFunction, isLoading } = useOrgFunction(orgFunctionId);
 *
 *   if (isLoading) return <Skeleton />;
 *   if (!orgFunction) return null;
 *
 *   return <div>{orgFunction.name}</div>;
 * }
 * ```
 */
export function useOrgFunction(id: string) {
  const { token } = useAuthStore();

  return useQuery({
    ...orgFunctionQueryOptions(id),
    enabled: !!token && !!id
  });
}

/**
 * Fetch the derived member list of a OrgFunction: the users who link to it via
 * mainRole or secondaryRoles. A lead who also holds it via one of those links
 * is flagged with `isLead`; a lead without such a link is not a member.
 *
 * Membership is never stored — the backend derives it on read.
 *
 * @example
 * ```tsx
 * function OrgFunctionMembers({ orgFunctionId }: { orgFunctionId: string }) {
 *   const { data: members, isLoading } = useOrgFunctionMembers(orgFunctionId);
 *
 *   if (isLoading) return <Skeleton />;
 *   if (!members?.length) return <Empty />;
 *
 *   return <MemberList members={members} />;
 * }
 * ```
 */
export function useOrgFunctionMembers(id: string) {
  const { token } = useAuthStore();

  return useQuery({
    queryKey: orgFunctionKeys.members(id),
    queryFn: async ({ signal }): Promise<OrgFunctionMember[]> => {
      const { data } = await apiClient.get<ListOrgFunctionMembersResponse>(
        listOrgFunctionMembersContract.path.replace(':id', id),
        { signal }
      );
      return data.data;
    },
    enabled: !!token && !!id
  });
}

/**
 * Fetch the current user's held OrgFunctions for the Meine Funktionen list: their
 * mainRole (the Stelle, first) + secondary roles + the OrgFunctions they lead.
 * Soft-deleted OrgFunctions are resolved (they carry `deletedAt`) so stale
 * links still render — this is a lookup surface, never a picker.
 *
 * @example
 * ```tsx
 * function MyOrgFunctions() {
 *   const { data: orgFunctions, isLoading } = useMyOrgFunctions();
 *
 *   if (isLoading) return <Skeleton />;
 *   if (!orgFunctions?.length) return <Empty />;
 *
 *   return <List items={orgFunctions} />;
 * }
 * ```
 */
export function useMyOrgFunctions() {
  const { token } = useAuthStore();

  return useQuery({
    queryKey: orgFunctionKeys.mine(),
    queryFn: async ({ signal }): Promise<HeldOrgFunctionRef[]> => {
      const { data } = await apiClient.get<ListMyOrgFunctionsResponse>(
        listMyOrgFunctionsContract.path,
        { signal }
      );
      return data.data;
    },
    enabled: !!token
  });
}
